import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'

import { Box, Stack, Typography, useTheme } from '@mui/material'
import { alpha } from '@mui/material/styles'
import {
  ArrowLeftRightIcon,
  BellIcon,
  BlocksIcon,
  Building2Icon,
  ContrastIcon,
  GiftIcon,
  HistoryIcon,
  MapPinIcon,
  PlugIcon,
  ReceiptIcon,
  ShieldCheckIcon,
  SlidersHorizontalIcon,
  ToggleLeftIcon,
  UserIcon,
  UsersIcon,
} from 'lucide-react'

import { Card, SideNav, Tabs } from '@repo/ui'

/**
 * Settings navigation — `Personal | Company` TABS (today) against SECTIONS (Vergence-style),
 * rendered together at real size so the choice can be looked at.
 *
 * ## Why side-by-side here, when the section-style lab used a toggle
 *
 * Opposite questions. That lab compared surface luminance and borders, where the differences
 * are a few percent of contrast and the eye needs the two states at the SAME position to
 * judge them — so a toggle. Here the entire question is structural and one of the answers is
 * a HEIGHT: does the whole list fit on screen at once, and can you see the shape of it. A
 * toggle hides exactly that, because you cannot compare two lengths you never see together.
 *
 * ## The measurement that decides it
 *
 * Sections mean no tabs, which means all 15 rows plus 2 headers are always rendered. Measured
 * in the browser at 1280x800, as an internal admin who sees every row:
 *
 *   A  tabs + pills (today)        329px
 *   B  sections + rail             582px
 *   C  sections + pills           852px
 *
 * Against 623px of nav column (MEASURED on the real page at 1280x800: nav top y=153, 647px to the
 * viewport bottom, less the 24px bottom gutter), C does NOT fit — so a
 * sectioned nav on today's 48px rows would scroll, and a scrolling nav loses the one thing
 * sections buy you: seeing the whole structure at once. At the rail's 34px it fits with ~126px
 * to spare. So the honest framing is not "tabs vs sections" but "tabs+pills vs sections+rail":
 * the row treatment is not a separate preference, it is a constraint that follows from
 * dropping the tabs.
 *
 * A is the shortest by a wide margin, which is worth saying plainly — if vertical space is the
 * only criterion, today's design wins. It buys that by not rendering half the destinations.
 *
 * Both heights are measured live below rather than asserted, because they depend on the
 * window and on how many rows the viewer's permissions actually show.
 *
 * ## What each option costs, so the decision is not made on looks alone
 *
 * TABS keep the nav short and make the Personal/Company split unmissable, but hide half the
 * destinations behind a click and spend a full row of vertical chrome plus a rule on a
 * two-item switch. They also make "where am I" ambiguous on a deep link: land on Billing and
 * the Personal items simply are not there.
 *
 * SECTIONS show every destination and every group at once and remove the tab chrome, at the
 * cost of a longer column and a weaker Personal/Company boundary — a section header is
 * quieter than a selected tab. The rail's continuous rule is what puts that boundary back:
 * it draws the list as one structure with parts, which is the claim sections are making.
 *
 * Variants are implemented LOCALLY. Only the winner should touch `SideNav`, which 2 pages
 * consume — same rule the section-style lab followed.
 */

const ICON_PROPS = { size: 16, strokeWidth: 1 } as const

/**
 * Mirrors `SettingsPage`'s PERSONAL_MENU / COMPANY_MENU. Duplicated on purpose: those are
 * module-private, and exporting them so a lab can read them would widen a page's API for a
 * throwaway comparison. The lab only needs labels, icons and order to be faithful — it does
 * not route, so drift in the routes it does not use cannot mislead the decision.
 */
const PERSONAL = [
  { label: 'Profile', icon: <UserIcon {...ICON_PROPS} /> },
  { label: 'Appearance', icon: <ContrastIcon {...ICON_PROPS} /> },
  { label: 'Notifications', icon: <BellIcon {...ICON_PROPS} /> },
  { label: 'Controls', icon: <SlidersHorizontalIcon {...ICON_PROPS} /> },
  { label: 'Refer & earn', icon: <GiftIcon {...ICON_PROPS} /> },
]

const COMPANY = [
  { label: 'General', icon: <Building2Icon {...ICON_PROPS} /> },
  { label: 'Installation', icon: <PlugIcon {...ICON_PROPS} /> },
  { label: 'Users', icon: <UsersIcon {...ICON_PROPS} /> },
  { label: 'Roles & permissions', icon: <ShieldCheckIcon {...ICON_PROPS} /> },
  { label: 'Features', icon: <ToggleLeftIcon {...ICON_PROPS} /> },
  { label: 'Billing', icon: <ReceiptIcon {...ICON_PROPS} /> },
  { label: 'Address book', icon: <MapPinIcon {...ICON_PROPS} /> },
  { label: 'Integrations', icon: <BlocksIcon {...ICON_PROPS} /> },
  { label: 'Fields', icon: <ArrowLeftRightIcon {...ICON_PROPS} /> },
  { label: 'Activity log', icon: <HistoryIcon {...ICON_PROPS} /> },
]

const toItems = (rows: typeof PERSONAL) =>
  rows.map(r => ({ label: r.label, value: r.label, icon: r.icon }))

/** Reports its own rendered height, so the fit claim is measured rather than asserted. */
const Measured = ({
  label,
  note,
  children,
}: {
  label: string
  note: string
  children: React.ReactNode
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const [h, setH] = useState<number | null>(null)

  // A layout effect with NO dependency array, deliberately: it re-measures after every
  // commit, and `setH` with an unchanged number is a no-op in React, so it converges
  // instead of looping.
  //
  // This was a ResizeObserver first, which is the obvious tool and did not work — a fresh
  // RO observing a live element in this preview fired 0 callbacks in 600ms, where the spec
  // says it should fire once immediately on observe. So every column reported "…" forever.
  // Not worth diagnosing further for a lab readout; what matters is that the number on
  // screen is measured rather than absent, and this path does not depend on RO firing.
  useLayoutEffect(() => {
    const next = ref.current?.getBoundingClientRect().height ?? null
    if (next !== null) setH(Math.round(next))
  })

  useEffect(() => {
    const onResize = () => setH(Math.round(ref.current?.getBoundingClientRect().height ?? 0))
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  return (
    // Fixed width, not `flex: 1`. With six columns and a 240px-wide nav inside each, flex
    // gave every cell ~160px and the navs overflowed their own cells and collided. A fit
    // comparison rendered on top of itself is worse than no comparison.
    <Stack spacing={3} sx={{ width: 248, flexShrink: 0 }}>
      <Stack spacing={1}>
        <Typography variant="body3" sx={{ fontWeight: 600 }}>
          {label}
        </Typography>
        <Typography variant="body4" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          {note}
        </Typography>
        <Typography variant="body4" sx={{ fontWeight: 600 }}>
          measured height: {h === null ? '…' : `${Math.round(h)}px`}
        </Typography>
      </Stack>
      <Box ref={ref} sx={{ width: 240 }}>
        {children}
      </Box>
    </Stack>
  )
}

/**
 * A section header inside the nav.
 *
 * Deliberately NOT styled like the docs sidebar's section headers, which are uppercase and
 * bold because that nav has five sections and needs them to shout. Two sections need only to
 * be distinguishable from the rows beneath them, and uppercase-bold on a two-item structure
 * reads as more hierarchy than exists. 11px/600/secondary with letter-spacing is enough to
 * read as a label rather than a destination, which is the only job.
 */
const SectionHeader = ({ children, first }: { children: React.ReactNode; first?: boolean }) => (
  <Typography
    variant="body4"
    color="text.secondary"
    sx={{
      display: 'block',
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      // 8px above the rows it labels, 20px of separation from the section before it. A
      // header sitting equidistant between two groups belongs to neither.
      pl: 3,
      pb: 2,
      pt: first ? 0 : 5,
    }}
  >
    {children}
  </Typography>
)

/**
 * A nav list with the three axes D/E/F need to vary independently: row height, how the selected
 * row is marked, and whether the group sits on a continuous rule.
 *
 * Local to the lab rather than added to `SideNav` as more variants, for the reason the other
 * labs give: only the winner should touch a component two pages consume. `SideNav` today offers
 * exactly two frozen combinations (pill at 48px, rail at 34px on a rule), and the whole point of
 * D/E/F is that those two bundle choices which are actually separable — height is not the same
 * decision as marker, and marker is not the same decision as rule.
 */
type RowMark = 'pill' | 'rail-on-rule' | 'rail-only'

const SUBTLE_TINT = 0.08

const LocalNav = ({
  rows,
  value,
  onChange,
  rowHeight,
  mark,
}: {
  rows: typeof PERSONAL
  value: string
  onChange: (v: string) => void
  rowHeight: number
  mark: RowMark
}) => {
  const theme = useTheme()
  const brand = theme.palette.brand?.[500] || '#8F68EA'
  const brandStrong = theme.palette.brand?.[600] || '#7F48DF'

  return (
    <Box
      sx={{
        // The continuous rule is what makes a rail read as ONE structure with parts rather than
        // a set of separate rows. Without it (rail-only) the 2px segment floats.
        borderLeft:
          mark === 'rail-on-rule'
            ? `1px solid ${alpha(theme.palette.text.primary, 0.16)}`
            : undefined,
      }}
    >
      {rows.map(row => {
        const selected = row.label === value
        return (
          <Stack
            key={row.label}
            direction="row"
            alignItems="center"
            spacing={3}
            onClick={() => onChange(row.label)}
            sx={{
              height: rowHeight,
              px: 3,
              cursor: 'pointer',
              // B's tint at both hover and selected — the "subtle purple" being carried across.
              backgroundColor: selected ? alpha(brand, SUBTLE_TINT) : 'transparent',
              '&:hover': {
                backgroundColor: alpha(brand, selected ? SUBTLE_TINT : SUBTLE_TINT / 2),
              },
              borderRadius: mark === 'pill' ? 2 : 0,
              // rail-on-rule overlaps the group's 1px rule by 1px so the 2px brand segment
              // replaces it rather than sitting beside it.
              ...(mark === 'rail-on-rule' ? { ml: '-1px' } : {}),
              borderLeft:
                mark === 'pill'
                  ? undefined
                  : `2px solid ${selected ? brandStrong : 'transparent'}`,
              color: selected ? theme.palette.text.primary : theme.palette.text.secondary,
              fontWeight: selected ? 600 : 400,
            }}
          >
            {row.icon}
            <Typography
              variant="body3"
              sx={{ fontWeight: 'inherit', color: 'inherit', fontSize: 15 }}
            >
              {row.label}
            </Typography>
          </Stack>
        )
      })}
    </Box>
  )
}

export const SettingsNavLab = () => {
  const theme = useTheme()
  // Both tabbed columns default to COMPANY deliberately. They were 'personal' and 'company',
  // which made A measure 329px against E's 549px — a 220px gap that was purely 5 rows vs 10,
  // not a difference between the options. A fit test has to use the worst case, and for a
  // tabbed nav the worst case is the longer tab.
  const [tab, setTab] = useState('company')
  const [tabsSel, setTabsSel] = useState('Profile')
  const [sectionsSel, setSectionsSel] = useState('Billing')
  const [pillsSel, setPillsSel] = useState('Billing')
  const [dSel, setDSel] = useState('Billing')
  const [eTab, setETab] = useState('company')
  const [eSel, setESel] = useState('Billing')
  const [fSel, setFSel] = useState('Billing')

  const tabItems = toItems(tab === 'personal' ? PERSONAL : COMPANY)

  return (
    <Stack spacing={6}>
      <Stack spacing={2}>
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          Settings navigation — tabs vs sections
        </Typography>
        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          Six options at real size, heights measured live, because the deciding fact is whether
          the list fits without scrolling. Every tabbed column sits on the COMPANY tab — the
          longer of the two — so the tabbed and sectioned options are compared at the same worst
          case rather than one showing 5 rows and the other 15.
        </Typography>
      </Stack>

      <Card sx={{ m: 0 }}>
        <Typography variant="body4" color="text.secondary" sx={{ lineHeight: 1.6 }}>
          The budget is 623px, measured on the real page rather than estimated: at 1280x800 the
          settings nav starts at y=153, leaving 647px to the viewport bottom and 623px once the
          24px bottom gutter every full-page element keeps is subtracted. An earlier version of
          this lab said &quot;about 708px&quot;, which was a guess and was 85px too generous —
          enough to have passed an option that does not actually fit. Compare each height against
          623, not against the other columns.
        </Typography>
      </Card>

      <Stack
        direction="row"
        spacing={8}
        sx={{ alignItems: 'flex-start', flexWrap: 'wrap', rowGap: 8 }}
      >
        {/* ---- A: today ---- */}
        <Measured
          label="A — tabs + pills (today)"
          note="Half the destinations are one click away. The tab row and its rule cost ~57px of chrome before the first destination."
        >
          <Stack>
            {/* `redirect={false}` is load-bearing: Tabs navigates on select by default, so
                without it clicking Company here would leave the lab for /settings. */}
            <Tabs
              redirect={false}
              value={tab}
              onChange={setTab}
              tabs={[
                { label: 'Personal', value: 'personal' },
                { label: 'Company', value: 'company' },
              ]}
            />
            <Box sx={{ pt: 4 }}>
              <SideNav items={tabItems} value={tabsSel} onChange={setTabsSel} />
            </Box>
          </Stack>
        </Measured>

        {/* ---- B: the proposal ---- */}
        <Measured
          label="B — sections + rail"
          note="Every destination visible. No tab chrome. The rail's continuous rule carries the grouping that the tabs used to state."
        >
          <Stack>
            <SectionHeader first>Personal</SectionHeader>
            <SideNav
              variant="rail"
              items={toItems(PERSONAL)}
              value={sectionsSel}
              onChange={setSectionsSel}
            />
            <SectionHeader>Company</SectionHeader>
            <SideNav
              variant="rail"
              items={toItems(COMPANY)}
              value={sectionsSel}
              onChange={setSectionsSel}
            />
          </Stack>
        </Measured>

        {/* ---- C: the option that does not fit, shown rather than described ---- */}
        <Measured
          label="C — sections + pills (does not fit)"
          note="The same structure on today's 48px rows. Included because 'sections' and 'smaller rows' sound like two independent choices, and this is the evidence that they are not."
        >
          <Stack>
            <SectionHeader first>Personal</SectionHeader>
            <SideNav items={toItems(PERSONAL)} value={pillsSel} onChange={setPillsSel} />
            <SectionHeader>Company</SectionHeader>
            <SideNav items={toItems(COMPANY)} value={pillsSel} onChange={setPillsSel} />
          </Stack>
        </Measured>

        {/* ---- D: B, but with room to breathe ---- */}
        <Measured
          label="D — sections + rail, larger rows (44px)"
          note="B's structure and continuous rule at 44px instead of 34px. Tests whether the rail's compactness was doing the work or just paying for the fit."
        >
          <Stack>
            <SectionHeader first>Personal</SectionHeader>
            <LocalNav rows={PERSONAL} value={dSel} onChange={setDSel} rowHeight={44} mark="rail-on-rule" />
            <SectionHeader>Company</SectionHeader>
            <LocalNav rows={COMPANY} value={dSel} onChange={setDSel} rowHeight={44} mark="rail-on-rule" />
          </Stack>
        </Measured>

        {/* ---- E: today's shape, B's colour ---- */}
        <Measured
          label="E — tabs + pills, B's subtle purple"
          note="A unchanged except the selected fill: brand at 8% instead of the 16% MUI's Mui-selected override applies today. The smallest possible change that fixes the loudness without touching the IA."
        >
          <Stack>
            <Tabs
              redirect={false}
              value={eTab}
              onChange={setETab}
              tabs={[
                { label: 'Personal', value: 'personal' },
                { label: 'Company', value: 'company' },
              ]}
            />
            <Box sx={{ pt: 4 }}>
              <LocalNav
                rows={eTab === 'personal' ? PERSONAL : COMPANY}
                value={eSel}
                onChange={setESel}
                rowHeight={48}
                mark="pill"
              />
            </Box>
          </Stack>
        </Measured>

        {/* ---- F: sections at full height, marker only on the selected row ---- */}
        <Measured
          label="F — sections + 48px rows, rail on selected only"
          note="C's height and structure with B's tint, and NO continuous rule: the 2px brand segment appears only on the selected row. Square rows, so the marker is the only chrome."
        >
          <Stack>
            <SectionHeader first>Personal</SectionHeader>
            <LocalNav rows={PERSONAL} value={fSel} onChange={setFSel} rowHeight={48} mark="rail-only" />
            <SectionHeader>Company</SectionHeader>
            <LocalNav rows={COMPANY} value={fSel} onChange={setFSel} rowHeight={48} mark="rail-only" />
          </Stack>
        </Measured>
      </Stack>

      <Card sx={{ m: 0, borderColor: alpha(theme.palette.text.primary, 0.08) }}>
        <Stack spacing={2}>
          <Typography variant="body3" sx={{ fontWeight: 600 }}>
            What is still unresolved, if B wins
          </Typography>
          <Typography variant="body4" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            Deep-link selection. With tabs, the URL picks the tab and the nav follows. With
            sections there is one flat list, so `matchMenuIndex` collapses to a single lookup
            and the two `selectedPersonal` / `selectedCompany` states become one — a
            simplification, but it touches the routing block, not just the markup.
          </Typography>
          <Typography variant="body4" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            Permission-hidden rows. Company rows are gated, so a limited user may see a
            Company section with two entries. That is fine, but an EMPTY section must not
            render its header — otherwise a user with no company permissions gets a heading
            labelling nothing.
          </Typography>
          <Typography variant="body4" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            Section headers belong in `SideNav` rather than in the page, if this ships: the
            docs nav already hand-rolls its own, which is two implementations of one pattern
            and the second one is the point at which it should be extracted.
          </Typography>
        </Stack>
      </Card>
    </Stack>
  )
}
